import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { REVIEW_TYPE_OF_PROVIDER } from '../../util/types';

const SHOPS_PAGE_SIZE = 48;
const SELL_LISTING_TYPES = ['book-listing', 'collector-merch'];

const hasShopName = seller => !!seller?.attributes?.profile?.publicData?.shopName;

export const buildShopDirectory = (listings, reviewsByUserId) =>
  listings
    .filter(
      listing =>
        !listing.attributes?.deleted &&
        listing.attributes?.state === 'published' &&
        (!listing.attributes?.publicData?.listingType ||
          SELL_LISTING_TYPES.includes(listing.attributes?.publicData?.listingType))
    )
    .reduce((shopsById, listing) => {
      const seller = listing.author;
      const sellerId = seller?.id?.uuid;
      if (!sellerId) {
        return shopsById;
      }

      const currentShop = shopsById[sellerId];
      const sellerWithShopName = hasShopName(currentShop?.seller) ? currentShop.seller : seller;
      const shop = currentShop || { seller: sellerWithShopName, listings: [], reviews: [] };
      shop.seller = sellerWithShopName;
      shop.listings.push(listing);
      shop.reviews = (reviewsByUserId[sellerId] || []).filter(
        review => review.attributes?.type === REVIEW_TYPE_OF_PROVIDER
      );
      return { ...shopsById, [sellerId]: shop };
    }, {});

const loadShopsPayloadCreator = ({ config }, { dispatch, rejectWithValue, extra: sdk }) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      perPage: SHOPS_PAGE_SIZE,
      page: 1,
      sort: '-createdAt',
      include: ['author', 'author.profileImage', 'images'],
      'fields.listing': [
        'title',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.genre',
        'publicData.editionsource',
        'publicData.merch_type',
      ],
      'fields.user': [
        'profile.displayName',
        'profile.publicData.shopName',
        'profile.publicData.province',
        'profile.publicData.ships_from',
        'profile.publicData.shopBio',
        'profile.publicData.shopSpecialties',
        'profile.publicData.bundleDiscountEnabled',
        'profile.publicData.bundleDiscountPercent',
        'profile.publicData.badges',
      ],
      'fields.image': [
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        'variants.square-small',
        'variants.square-small2x',
        'variants.square-xsmall',
        'variants.square-xsmall2x',
      ],
      ...createImageVariantConfig(variantPrefix, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      minStock: 1,
      stockMode: 'match-undefined',
      'limit.images': 5,
    })
    .then(response => {
      const listings = denormalisedResponseEntities(response).filter(
        listing =>
          !listing.attributes?.deleted &&
          listing.attributes?.state === 'published' &&
          (!listing.attributes?.publicData?.listingType ||
            SELL_LISTING_TYPES.includes(listing.attributes?.publicData?.listingType))
      );
      dispatch(addMarketplaceEntities(response));

      return Promise.all(
        [...new Set(listings.map(listing => listing.author?.id?.uuid).filter(Boolean))].map(
          userId =>
            sdk.reviews
              .query({ subject_id: userId, state: 'public' })
              .then(reviewResponse => [userId, denormalisedResponseEntities(reviewResponse)])
        )
      ).then(reviewEntries => ({
        listings,
        reviewsByUserId: Object.fromEntries(reviewEntries),
      }));
    })
    .catch(error => rejectWithValue(storableError(error)));
};

export const loadShops = createAsyncThunk('ShopsPage/loadShops', loadShopsPayloadCreator);

const initialState = {
  listings: [],
  reviewsByUserId: {},
  error: null,
};

const shopsPageSlice = createSlice({
  name: 'ShopsPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadShops.pending, state => {
        state.error = null;
      })
      .addCase(loadShops.fulfilled, (state, action) => {
        state.listings = action.payload.listings;
        state.reviewsByUserId = action.payload.reviewsByUserId;
      })
      .addCase(loadShops.rejected, (state, action) => {
        state.listings = [];
        state.reviewsByUserId = {};
        state.error = action.payload;
      });
  },
});

export const loadData = (_params, _search, config) => dispatch => dispatch(loadShops({ config }));

export default shopsPageSlice.reducer;
